/**
 * Hardcoded approval-status options shared by a sample's Licensing and Customer
 * status controls (BM-9882). The backend enforces the same four values (see
 * `data-access-layer/src/schemas/Sample.ts` and the sample service whitelist);
 * keep the two lists in step.
 *
 * `value` is what is persisted and sent to the API. `labelKey` resolves through
 * i18n for display. `swatchClass` reuses the existing `.col-status right <class>`
 * pill classes so the licensing/customer pills match the app's status swatches.
 */

export type SampleApprovalStatus = 'pending' | 'approved' | 'approved with comments' | 'rejected';

export interface SampleApprovalStatusOption {
  value: SampleApprovalStatus;
  labelKey: string;
  swatchClass: string;
}

export const SAMPLE_APPROVAL_STATUS_DEFAULT: SampleApprovalStatus = 'pending';

export const SAMPLE_APPROVAL_STATUS_OPTIONS: SampleApprovalStatusOption[] = [
  { value: 'pending', labelKey: 'DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.APPROVAL_STATUS.PENDING', swatchClass: 'pending' },
  { value: 'approved', labelKey: 'DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.APPROVAL_STATUS.APPROVED', swatchClass: 'approved' },
  {
    value: 'approved with comments',
    labelKey: 'DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.APPROVAL_STATUS.APPROVED_WITH_COMMENTS',
    swatchClass: 'revised',
  },
  { value: 'rejected', labelKey: 'DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.APPROVAL_STATUS.REJECTED', swatchClass: 'rejected' },
];

/**
 * Resolves the swatch CSS class for a stored approval status, falling back to the
 * default's swatch when the value is missing or unrecognised.
 *
 * @param {string | null | undefined} status - Stored approval-status value.
 * @returns {string} The `.col-status right <class>` swatch class name.
 */
export const getSampleApprovalSwatchClass = (status?: string | null): string => {
  const option = SAMPLE_APPROVAL_STATUS_OPTIONS.find((item) => item.value === status);
  return option?.swatchClass ?? 'pending';
};
